import type { ExperienceStatus } from "@intelligems/ig-types";

/** Save behavior follows publication history; liveness only describes usage. */

/**
 * The assignment fields liveness is decided from — structurally a subset of
 * `AgenticLogicPlacementAssignmentDto`, so the list read hands its rows
 * straight in.
 *
 * Declared structurally rather than as the DTO itself for the usual reason a
 * pure helper does: the decision depends on four fields, and a spec that has
 * to build a whole assignment row (placement ids, budgets, timestamps) to
 * exercise "paused experience" is testing the fixture, not the rule.
 */
export interface RuleLivenessAssignment {
  ruleId: string;
  /** A row the merchant switched off serves nothing, whatever it points at. */
  enabled: boolean;
  /**
   * `null` for a store-wide row, and also for a variation-scoped row whose
   * experience is gone — `variationId` is what tells the two apart.
   */
  variationId: string | null;
  /**
   * The experience this row serves, resolved at read time. `null` on a
   * store-wide row (there is no experience) and on a row whose variation was
   * torn down (`variationId` is deliberately not a foreign key, so an
   * assignment outlives the experiment it was made for).
   */
  experience: { status: ExperienceStatus } | null;
}

/**
 * What the rule is doing on the storefront right now.
 *
 * `unknown` is its own answer rather than a default, because the two callers
 * want it to mean different things and neither wants it silently folded into
 * `not_live`: the assignments list is a separate query from the rule, and for
 * the first frames of a page load nobody knows what the rule serves.
 */
export type RuleLiveness = "live" | "not_live" | "unknown";

/** What Save does: the old persist-and-deploy, or a draft write on its own. */
export type RuleSaveAction = "save_and_publish" | "save_draft_only";

/**
 * Whether ONE assignment row is putting the rule in front of shoppers.
 *
 * Two ways to be live, and only two:
 *
 * - a variation-scoped row whose experience has `status === "started"`.
 *   `pending` has never run, `paused` is not running and `ended` is over —
 *   none of the three reaches a shopper, so none of them is live.
 * - a store-wide row (`variationId === null`). It has no experience to carry a
 *   status because it is not scoped to one: it is the placement's default and
 *   serves every shopper the experience rows do not claim. Reading its null
 *   `experience` as "not live" would hand the whole global-assignment case
 *   back to auto-publish, which is the case this batch exists for.
 *
 * A variation-scoped row with a null `experience` is the torn-down variation:
 * the experiment it named no longer exists, so it serves nobody and is not
 * live. That is a different fact from the global row above and the DTO keeps
 * `variationId` populated precisely so the two stay distinguishable.
 */
const isServing = (assignment: RuleLivenessAssignment): boolean => {
  if (!assignment.enabled) {
    return false;
  }
  if (assignment.variationId === null) {
    return true;
  }
  return assignment.experience?.status === "started";
};

/**
 * Whether this rule is serving shoppers, from the org-wide assignment list.
 *
 * `assignments` is `undefined` while the list read is in flight or after it
 * failed — the caller passes the query's data through untouched, and a read
 * that has not answered must not be reported as "attached to nothing". The
 * same reasoning `useRuleDetailByline` gives for keeping its halves nullable:
 * an unloaded read that looked like "used nowhere" would wave a save straight
 * past the consent it exists to ask for.
 */
export const ruleLiveness = ({
  ruleId,
  assignments,
}: {
  ruleId: string;
  assignments: readonly RuleLivenessAssignment[] | undefined;
}): RuleLiveness => {
  if (assignments === undefined) {
    return "unknown";
  }
  const own = assignments.filter((assignment) => assignment.ruleId === ruleId);
  return own.some(isServing) ? "live" : "not_live";
};

/** Creation saves and publishes together. Every later Save writes only the draft.
 * An active version remains selected while a rule is paused or unused.
 * An unresolved version read must not permit an automatic publish.
 */
export const resolveRuleSaveAction = (input: {
  ruleId: string;
  assignments: readonly RuleLivenessAssignment[] | undefined;
  hasActiveVersion: boolean | undefined;
}): RuleSaveAction =>
  input.hasActiveVersion === false ? "save_and_publish" : "save_draft_only";
